import { useEffect, useState } from "react";
import { OutletHeroesDataType, languagesDataType } from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Select } from "antd";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { OutletHeroesService } from "../../services/outlet_heroes.service";
import SelectImage from "../../components/shared/select-image/SelectImage";
import ShowImage from "../../components/shared/show-image/ShowImage";

const OutletHeroes = () => {
  const [hero, setHero] = useState<OutletHeroesDataType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const fetchHero = () => {
    setLoading(true);
    OutletHeroesService.getOutletHeroes()
      .then((response) => {
        const data = response?.data?.data ?? response?.data ?? null;
        setHero(Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchHero();
  }, []);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);

  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        image: null as any,
        status: "1",
        sort_order: "1",
        translations: [] as {
          locale: string;
          title_1: string;
          title_2: string;
          description: string;
        }[],
        title_1: "",
        title_2: "",
        description: "",
      },
      onSubmit: async (vals) => {
        const azTranslation = {
          locale: "az",
          title_1: vals.title_1,
          title_2: vals.title_2,
          description: vals.description,
        };
        const updatedTranslations = [
          azTranslation,
          ...vals.translations.filter((t) => t.locale !== "az"),
        ];

        const formData = new FormData();
        if (vals.image && typeof vals.image !== "string") {
          formData.append("image", vals.image);
        }
        formData.append("status", vals.status);
        formData.append("sort_order", vals.sort_order);

        updatedTranslations.forEach((t, i) => {
          formData.append(`translations[${i}][locale]`, t.locale);
          formData.append(`translations[${i}][title_1]`, t.title_1);
          formData.append(`translations[${i}][title_2]`, t.title_2);
          formData.append(`translations[${i}][description]`, t.description);
        });

        if (isCreating || !hero) {
          const response = await OutletHeroesService.createOutletHeroes(formData);
          if (response.status === 200 || response.status === 201) {
            message.success("Outlet Hero uğurla əlavə edildi!");
            setIsModalVisible(false);
            resetForm();
            setLogoFileList([]);
            fetchHero();
          } else {
            message.error(response.errorMessage || "Əlavə etmək alınmadı.");
          }
        } else {
          const response = await OutletHeroesService.updateOutletHeroes(
            hero.id,
            formData
          );
          if (response.status === 200 || response.status === 201) {
            message.success("Outlet Hero uğurla yeniləndi!");
            setIsModalVisible(false);
            resetForm();
            setLogoFileList([]);
            fetchHero();
          } else {
            message.error(response.errorMessage || "Yeniləmək alınmadı.");
          }
        }
      },
    });

  const handleTranslationChange = (
    value: string,
    field: "title_1" | "title_2" | "description"
  ) => {
    if (langCode === "AZ") {
      setFieldValue(field, value);
    } else {
      const locale = langCode.toLowerCase();
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.locale === locale
      );
      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          locale,
          title_1: field === "title_1" ? value : "",
          title_2: field === "title_2" ? value : "",
          description: field === "description" ? value : "",
        });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleLanguageButtonClick = (lang: string) => {
    setLangCode(lang);
  };

  const getTranslationValue = (
    field: "title_1" | "title_2" | "description"
  ) => {
    if (langCode === "AZ") {
      return values[field];
    }
    const locale = langCode.toLowerCase();
    return (
      values.translations.find((t) => t.locale === locale)?.[field] ?? ""
    );
  };

  const openCreateModal = () => {
    setIsCreating(true);
    resetForm();
    setLogoFileList([]);
    setLangCode("AZ");
    setIsModalVisible(true);
  };

  const openEditModal = () => {
    if (!hero) return;
    setIsCreating(false);
    const azTrans = hero.translations.find(
      (t) => t.locale === "az" || t.locale === "AZ"
    );
    const otherTrans = hero.translations.filter(
      (t) => t.locale !== "az" && t.locale !== "AZ"
    );
    setFieldValue("title_1", azTrans?.title_1 ?? "");
    setFieldValue("title_2", azTrans?.title_2 ?? "");
    setFieldValue("description", azTrans?.description ?? "");
    setFieldValue("translations", otherTrans);
    const statusVal = hero.status === true || hero.status === "1" || hero.status === 1 ? "1" : "0";
    setFieldValue("status", statusVal);
    setFieldValue("sort_order", hero.sort_order?.toString() ?? "1");
    setFieldValue("image", hero.image_url ?? null);
    setLogoFileList(
      hero.image_url
        ? [
            {
              uid: "-1",
              name: "hero image",
              status: "done",
              url: hero.image_url,
            },
          ]
        : []
    );
    setLangCode("AZ");
    setIsModalVisible(true);
  };

  const handleDelete = async () => {
    if (!hero) return;
    const response = await OutletHeroesService.deleteOutletHeroes(hero.id);
    if (response.status === 200 || response.status === 204) {
      message.success("Outlet Hero silindi!");
      setHero(null);
    } else {
      message.error(response.errorMessage || "Silmək alınmadı.");
    }
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Outlet Hero</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Outlet Hero</h3>
          </div>

          <div className="pages_components">
            {!hero ? (
              <MainButton onClick={openCreateModal}>
                <FaPlus size={20} />
                &nbsp; Yenisini yarat
              </MainButton>
            ) : (
              <div style={{ display: "flex", gap: "12px" }}>
                <MainButton onClick={openEditModal}>
                  <FaEdit size={20} />
                </MainButton>
                <Popconfirm
                  title="Outlet Hero-nu silmək istəyirsiniz?"
                  onConfirm={handleDelete}
                  onCancel={() => message.info("Silmə ləğv edildi.")}
                  okText="Bəli"
                  cancelText="Xeyr"
                  placement="bottomLeft"
                >
                  <MainButton>
                    <FaTrash size={20} />
                  </MainButton>
                </Popconfirm>
              </div>
            )}

            {hero && (
              <div className="pages_table" style={{ marginTop: "24px" }}>
                {hero.image_url && (
                  <div style={{ marginBottom: "20px" }}>
                    <img
                      src={hero.image_url}
                      alt="hero"
                      style={{
                        width: "100%",
                        maxWidth: "600px",
                        height: "340px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedImage(hero.image_url);
                        setShowImageModal(true);
                      }}
                    />
                  </div>
                )}
                
                <div style={{ marginBottom: "20px", display: "flex", gap: "20px" }}>
                  <div>
                    <strong>Status:</strong> {hero.status === true || hero.status === "1" || hero.status === 1 ? "Aktiv" : "Deaktiv"}
                  </div>
                  <div>
                    <strong>Sıra (Sort Order):</strong> {hero.sort_order}
                  </div>
                </div>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "12px",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={thStyle}>Dil</th>
                      <th style={thStyle}>Başlıq 1</th>
                      <th style={thStyle}>Başlıq 2</th>
                      <th style={thStyle}>Açıqlama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hero.translations.map((t, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{t.locale?.toUpperCase()}</td>
                        <td style={tdStyle}>{t.title_1}</td>
                        <td style={tdStyle}>{t.title_2}</td>
                        <td style={tdStyle}>{t.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        title={isCreating ? "Yeni Outlet Hero əlavə edin" : "Outlet Hero-nu redaktə edin"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          resetForm();
          setLogoFileList([]);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="langs">
            {langs?.map((lang, index) => (
              <div key={index}>
                <button
                  className={lang.code === langCode ? "active_lang_btn" : ""}
                  type="button"
                  onClick={() => handleLanguageButtonClick(lang.code)}
                >
                  {lang.name}
                </button>
              </div>
            ))}
          </div>

          <div className="form_input">
            <label className="label" htmlFor="title_1">
              Başlıq 1 ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="title_1"
                name="title_1"
                placeholder="Başlıq 1"
                value={getTranslationValue("title_1")}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title_1")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title_1} touched={touched.title_1} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="title_2">
              Başlıq 2 ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="title_2"
                name="title_2"
                placeholder="Başlıq 2"
                value={getTranslationValue("title_2")}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title_2")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title_2} touched={touched.title_2} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="description">
              Açıqlama ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="description"
                name="description"
                placeholder="Açıqlama"
                value={getTranslationValue("description")}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.description}
              touched={touched.description}
            />
          </div>

          <div className="form_input">
            <label className="label">Status</label>
            <Select
              style={{ width: "100%", height: "40px" }}
              value={values.status}
              onChange={(val) => setFieldValue("status", val)}
              options={[
                { value: "1", label: "Aktiv" },
                { value: "0", label: "Deaktiv" },
              ]}
            />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="sort_order">
              Sıra (Sort Order)
            </label>
            <div className="input_wrapper">
              <input
                id="sort_order"
                name="sort_order"
                type="number"
                placeholder="Sıra"
                value={values.sort_order}
                onChange={(e) => setFieldValue("sort_order", e.target.value)}
              />
              <span className="focus_border"></span>
            </div>
          </div>

          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage error={errors.image as string | undefined} touched={touched.image as boolean | undefined} />
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isCreating ? "Əlavə edin" : "Redaktə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

const thStyle: React.CSSProperties = {
  border: "1px solid #e0e0e0",
  padding: "10px 14px",
  textAlign: "left",
  background: "#f5f5f5",
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  border: "1px solid #e0e0e0",
  padding: "10px 14px",
};

export default OutletHeroes;
